import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useCurrency from '../../yoello-lib/modules/localisation/useCurrency';

import moment from 'moment';
import { COUNTRIES } from "../../formSchemas/details.form";

import APIDefintions from '../../yoello-lib/modules/API2/APIDefs';
import { useProductsData } from "../../components/menu/products/useProductsData";
import ISubCategory from "../../components/menu/categories/ISubCategory";
import ICategory from "../../components/menu/categories/ICategory";
import { IReduxAppState } from "../../reducers";
// import { useAuth0 } from "../../yoello-lib/modules/auth/Auth0";
import useAPI from "../../yoello-lib/modules/API/useAPI";
import { APIExtensions } from "../../yoello-lib/modules/API/APIDefs";
import { useDispatch } from "react-redux";

import { generateTitle } from './Formatters';
import { fetchTopFive, fetchTotalsList, fetchRushData } from './APIHandlers';

import * as DASHBOARD_CONFIG from '../../config/dashboard';
import AnalyticsDashboardLayout from '../../yoello-lib/modules/components/Analytics/AnalyticsDashboardLayout/AnalyticsDashboardLayout';
import IVenue from "../../yoello-lib/modules/API/interfaces/IVenue";
import { setSidebar } from "../../actions/sidebar";
import * as URLS from "../../const/URL.const"
import { useDetailsFilled } from '../../hooks/useDetailsFilled';
import { useAuthenticationContext } from '../../yoello-lib/modules/auth/AuthenticationContext';

interface AnalyticsProps {
    allVenues?: boolean;
    venueList?: IVenue[];
    onAlertClick?: () => void;
}

const AnalyticsDashboardView = ({ allVenues = false, venueList = [], onAlertClick }: AnalyticsProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const selectedVenueId = useSelector(
        (state: IReduxAppState) => state.selectedVenueId
    );
    const currentLanguage = useSelector(
        (state: IReduxAppState) => state.language.current
    );
    useDetailsFilled();
    const [subCategories, setSubCategories] = useState<ISubCategory[]>();
    const [categories, setCategories] = useState<ICategory[]>();
    const [isSquareVenue, setIsSquareVenue] = useState(false);
    const [formattedCategories, setFormattedCategories] = useState<{ id: string, name: string }[]>([]);
    const [formattedSubCategories, setFormattedSubCategories] = useState<{ id: string, name: string }[]>([]);
    useProductsData<ISubCategory>(
        APIDefintions.menuItemSubcategories, selectedVenueId, (item) => setSubCategories(item), { 'sort_field': 'sort_position' });
    const { isLoading: isLoadingCategories } = useProductsData<ICategory>(
        APIDefintions.menuItemCategories, undefined, (item) => setCategories(item));
    const venue = useSelector((state: IReduxAppState) => state.venue[selectedVenueId]?.venueData);
    const { retrieveAccessToken } = useAuthenticationContext();

    const { formatCurrency } = useCurrency();
    const [catTotalValue, setCatTotalValue] = useState<string>("0");
    const [totalsList, setTotalsList] = useState<IAnalyticsTotal[]>(DASHBOARD_CONFIG.TOTALS_LIST.items);
    const [topFive, setTopFive] = useState<ITopFive[]>([]);
    const [rushData, setRushData] = useState<number[][]>([]);

    const [topFiveLoading, setTopFiveLoading] = useState<boolean>(true);
    const [rushDataLoading, setRushDataLoading] = useState<boolean>(true);
    const [totalsListLoading, setTotalsListLoading] = useState<boolean>(true);

    const [category, setCategory] = useState<string>('');
    const [startDate, setStartDate] = useState<Date | number>(new Date().setHours(0, 0, 0, 0));
    const [endDate, setEndDate] = useState<Date | number>(new Date().setHours(23, 59, 59, 59));
    const [seriesName, setSeriesName] = useState<string>(t("misc.orderCount"));
    const [seriesRounding, setSeriesRounding] = useState<number>(0);

    const [lineChartTitle, setLineChartTitle] = useState(<span></span>)
    const [totalsTitle, setTotalsTitle] = useState(<span></span>)
    const [categoriesTitle, setCategoriesTitle] = useState(<span></span>)

    const [venueSeparateStatistics, setVenueSeparateStatistics] = useState<Array<any>>([]);

    const topFiveHandler = useAPI(APIExtensions.analyticsTopFiveSales, retrieveAccessToken, {
        doNotCacheValue: true,
        onMountDisabled: true
    });
    const totalsHandler = useAPI(APIExtensions.analyticsTotalsList, retrieveAccessToken, {
        doNotCacheValue: true,
        onMountDisabled: true
    });
    const rushHandler = useAPI(APIExtensions.analyticsRushData, retrieveAccessToken, {
        doNotCacheValue: true,
        onMountDisabled: true
    });

    const onDateChange = (start: number | Date, end: number | Date) => {
        setStartDate(start);
        setEndDate(end);
    };
    const onTopFiveReceived = (topFive: ITopFive[], total: string) => {
        setTopFiveLoading(false);
        setCatTotalValue(total);
        setTopFive(topFive);
    }

    const onRushDataReceived = (rushData: number[][]) => {
        setRushDataLoading(false);
        setRushData(rushData);
    }

    const onTotalsReceived = (totals: IAnalyticsTotal[], separateVenuesData?: any) => {
        setVenueSeparateStatistics(separateVenuesData)
        setTotalsListLoading(false);
        const newTotalList = [...totals];
        totalsList.forEach((item, index) => {
            if (newTotalList[index].id === item.id) {
                newTotalList[index].tooltip = item.tooltip
            }
        })

        setTotalsList(newTotalList);
    }
    const areDatesDifferent = (startDate: Date | number, endDate: Date | number) => {
        const mStart = moment(startDate);
        const mEnd = moment(endDate);
        if (mStart.date() === mEnd.date()) {
            return false;
        }
        return true;
    }

    const categorySummary = {
        listTitle: t(`analytics.top.${category}`),
        title: categoriesTitle,
        totalValue: catTotalValue,
        selection: category,
        topFiveItems: topFive,
        loading: topFiveLoading,
        onCategoryChange: (value: string) => { setCategory(value) },
        categories: allVenues ? formattedCategories : isSquareVenue ? formattedSubCategories : formattedCategories,
    }
    const lineChart = {
        title: lineChartTitle,
        config: DASHBOARD_CONFIG.LINECHART,
        data: rushData,
        loading: rushDataLoading,
        seriesName: seriesName,
        seriesRounding: seriesRounding,
        noDataText: t("analytics.rush.noData"),
    }
    const columnChart = {
        title: lineChartTitle,
        config: DASHBOARD_CONFIG.COLUMNCHART(venueList.length),
        data: venueSeparateStatistics,
        loading: totalsListLoading,
        seriesName: seriesName,
        seriesRounding: seriesRounding,
        noDataText: t("analytics.columnChart.noData"),
    }


    const totalConfig = {
        title: totalsTitle,
        items: totalsList,
        loading: totalsListLoading
    };

    useEffect(() => {
        setLineChartTitle(
            generateTitle(t, t(allVenues ? 'analytics.columnChart.title' : 'analytics.rush.title'), startDate, endDate)
        );
        setTotalsTitle(
            generateTitle(t, t('analytics.totals.title'), startDate, endDate)
        );
        setCategoriesTitle(
            generateTitle(t, t('analytics.categories.title'), startDate, endDate)
        )
        // eslint-disable-next-line
    }, [startDate, endDate]);

    useEffect(() => {
        if (allVenues && venueList.length === 0) return;
        fetchTotalsList(totalsHandler, allVenues ? venueList as any : [venue], startDate, endDate, onTotalsReceived, formatCurrency);
        fetchRushData(rushHandler, venue, startDate, endDate, onRushDataReceived);
        setSeriesName(areDatesDifferent(startDate, endDate) ? t("misc.averageOrders") : t("misc.orderCount"))
        setSeriesRounding(areDatesDifferent(startDate, endDate) ? 2 : 0)
        // eslint-disable-next-line
    }, [startDate, endDate, currentLanguage, allVenues ? venueList : undefined]);

    useEffect(() => {
        setTopFiveLoading(true);
        if (category !== '') {
            fetchTopFive(topFiveHandler, allVenues ? venueList as any : [venue], startDate, endDate, category.toLowerCase(), onTopFiveReceived, formatCurrency, isSquareVenue);
        }
        // eslint-disable-next-line
    }, [category, isLoadingCategories, allVenues ? venueList : undefined])

    useEffect(() => {
        const formattedCats = categories?.filter((category: ICategory) => category.category_name !== "MENU").map((category: ICategory) => {
            return {
                id: category.category_name,
                name: category.category_name
            }
        })
        const formattedSubCats = subCategories?.map((subcategory: ISubCategory) => {
            return {
                id: subcategory.subcategory_name,
                name: subcategory.subcategory_name
            }
        })
        if (formattedCats && !isSquareVenue) {
            setFormattedCategories(formattedCats);
            if (formattedCats[0]) {
                setCategory(formattedCats[0].id);
            }
        }
        if (formattedSubCats && isSquareVenue) {
            setFormattedSubCategories(formattedSubCats);
            if (formattedSubCats[0]) {
                setCategory(formattedSubCats[0].id);
            }
        }

        //eslint-disable-next-line    
    }, [categories, subCategories])

    useEffect(() => {
        if (venue?.payment_processor === "SQUARE") {
            setIsSquareVenue(true);
        }
        if (allVenues) {
            dispatch(setSidebar({
                text: 'Overview',
                key: "OVERVIEW",
                url: URLS.VENUES_OVERVIEW,
                icon: undefined
            }))
        }
        //eslint-disable-next-line    
    }, [venue])

    return (
        <div>
            <AnalyticsDashboardLayout
                categorySummary={{ ...categorySummary }}
                lineChart={allVenues ? { ...columnChart } : { ...lineChart }}
                totalsList={{ ...totalConfig }}
                onDateChange={onDateChange}
                allVenues={allVenues}
                separateVenueData={venueSeparateStatistics}
                locale={getLocale(currentLanguage)}
                onAlertClick={onAlertClick}
            />
        </div>
    )
}

function getLocale(currentLanguage: string) {
    const country = COUNTRIES.find((COUNTRY: any) => {
        return COUNTRY.code.toLowerCase() === currentLanguage.split('-').pop();
    });

    return country?.locale;
}

export default AnalyticsDashboardView;